"use client";
import { jsxs, jsx } from "react/jsx-runtime";
import React, { useState, useCallback } from "react";
import { useTracking } from "react-tracking";
import Collapse from "@mui/material/Collapse";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ModalCloseButton } from "../Modal/ModalCloseButton/index.js";
import { PopupWrapper, StyledSlide, StyledPopup, TitleWrapper, TitleContainer, StyledTitleText, ButtonWrapper, ShowHideButton, StyledShowHideText, StyledChevronDown, StyledChevronUp } from "./styles.js";
import { PopupPositionRight } from "./types.js";
const COMPONENT_NAME = "Popup";
const Popup = ({
  announcer,
  closeButtonAriaLabel = "",
  contentRef,
  defaultExpanded = false,
  enableEntryAnimation = false,
  hideButtonId = "",
  hideLabelText = "Hide",
  isCloseable = true,
  isNonCloseOrCollapsible = false,
  displayOnAllScreenSize = false,
  open,
  onClose,
  onHide,
  onShow,
  popupAriaLabel,
  popupName = "",
  position = PopupPositionRight,
  renderContent,
  showButtonId = "",
  showLabelText = "Show",
  strategy = "fixed",
  sx,
  titleSx,
  title = "",
  titleId = "",
  ...restOfProps
}) => {
  const containerRef = React.useRef(null);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const isMobile = useMediaQuery(theme.breakpoints.between("xs", "md"));
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const { trackEvent } = useTracking();
  const handleClose = useCallback(() => {
    onClose == null ? void 0 : onClose();
    trackEvent({ component: { trackedData: ["Close"] } });
  }, [onClose, trackEvent]);
  if (!popupName) {
    console.error(`${COMPONENT_NAME}-error: popupName prop is required.`);
    return null;
  }
  if (isCloseable === void 0 || isCloseable === null) {
    console.error(`${COMPONENT_NAME}-error: isCloseable prop is required.`);
    return null;
  }
  if (!isCloseable && !isNonCloseOrCollapsible && !showLabelText) {
    console.error(
      `${COMPONENT_NAME}-error: isCloseable is false, but required prop showLabelText wasn't provided.`
    );
    return null;
  }
  if (!isCloseable && !isNonCloseOrCollapsible && !hideLabelText) {
    console.error(
      `${COMPONENT_NAME}-error: isCloseable is false, but required prop hideLabelText wasn't provided.`
    );
    return null;
  }
  if (isCloseable && !closeButtonAriaLabel) {
    console.error(
      `${COMPONENT_NAME}-error: isCloseable is true, but required prop closeButtonAriaLabel wasn't provided.`
    );
    return null;
  }
  if (!isCloseable && !isNonCloseOrCollapsible && !showButtonId) {
    console.error(
      `${COMPONENT_NAME}-error: isCloseable is false, but required prop showButtonId wasn't provided.`
    );
    return null;
  }
  if (!isCloseable && !isNonCloseOrCollapsible && !hideButtonId) {
    console.error(
      `${COMPONENT_NAME}-error: isCloseable is false, but required prop hideButtonId wasn't provided.`
    );
    return null;
  }
  if (!title) {
    console.error(`${COMPONENT_NAME}-error: title prop is required.`);
    return null;
  }
  if (!titleId) {
    console.error(`${COMPONENT_NAME}-error: titleId prop is required.`);
    return null;
  }
  if (!position) {
    console.error(`${COMPONENT_NAME}-error: position prop is required.`);
    return null;
  }
  if (!announcer) {
    console.error(`${COMPONENT_NAME}-error: announcer prop is required.`);
    return null;
  }
  const toggleShowHide = () => {
    if (isExpanded) {
      onHide == null ? void 0 : onHide();
    } else {
      onShow == null ? void 0 : onShow();
    }
    setIsExpanded(!isExpanded);
  };
  const TitledWrapper = /* @__PURE__ */ jsxs(TitleWrapper, { sx: titleSx, children: [
    /* @__PURE__ */ jsx(TitleContainer, { children: /* @__PURE__ */ jsx(StyledTitleText, { component: "h2", id: titleId, children: title }) }),
    !isNonCloseOrCollapsible && /* @__PURE__ */ jsx(ButtonWrapper, { children: isCloseable ? /* @__PURE__ */ jsx(
      ModalCloseButton,
      {
        ariaLabel: closeButtonAriaLabel,
        onClose: handleClose
      }
    ) : /* @__PURE__ */ jsxs(
      ShowHideButton,
      {
        variant: "tertiary",
        onClick: toggleShowHide,
        id: isExpanded ? hideButtonId : showButtonId,
        "aria-expanded": isExpanded,
        "aria-labelledby": `${isExpanded ? hideButtonId : showButtonId} ${titleId}`,
        children: [
          /* @__PURE__ */ jsx(StyledShowHideText, { variant: "t5", children: isExpanded ? hideLabelText : showLabelText }),
          isExpanded ? /* @__PURE__ */ jsx(StyledChevronDown, {}) : /* @__PURE__ */ jsx(StyledChevronUp, {})
        ]
      }
    ) })
  ] });
  const commonPopupProps = {
    "aria-label": popupAriaLabel,
    "aria-labelledby": !popupAriaLabel ? titleId : void 0,
    "data-testid": (restOfProps == null ? void 0 : restOfProps["data-testid"]) ?? COMPONENT_NAME,
    name: popupName,
    role: "region",
    sx,
    ...restOfProps
  };
  return /* @__PURE__ */ jsxs(
    PopupWrapper,
    {
      popupPosition: position,
      ref: containerRef,
      strategy,
      children: [
        announcer,
        (isCloseable && (isDesktop || displayOnAllScreenSize) || isNonCloseOrCollapsible) && /* @__PURE__ */ jsx(
          StyledSlide,
          {
            direction: "up",
            in: open,
            container: (containerRef == null ? void 0 : containerRef.current) || void 0,
            timeout: open ? 500 : 300,
            ...commonPopupProps,
            children: /* @__PURE__ */ jsxs(
              StyledPopup,
              {
                isOpenView: true,
                ref: contentRef,
                tabIndex: -1,
                children: [
                  TitledWrapper,
                  renderContent(handleClose)
                ]
              }
            )
          }
        ),
        !isCloseable && !isNonCloseOrCollapsible && ((isDesktop || displayOnAllScreenSize) && enableEntryAnimation ? /* @__PURE__ */ jsx(
          StyledSlide,
          {
            direction: "up",
            in: open,
            container: (containerRef == null ? void 0 : containerRef.current) || void 0,
            timeout: open ? 300 : 0,
            ...commonPopupProps,
            children: /* @__PURE__ */ jsxs(
              StyledPopup,
              {
                isOpenView: isExpanded,
                ref: contentRef,
                tabIndex: -1,
                children: [
                  TitledWrapper,
                  /* @__PURE__ */ jsx(
                    Collapse,
                    {
                      in: isExpanded,
                      timeout: isExpanded ? 500 : 300,
                      children: renderContent(handleClose)
                    }
                  )
                ]
              }
            )
          }
        ) : (!isMobile || displayOnAllScreenSize) && open && /* @__PURE__ */ jsxs(
          StyledPopup,
          {
            isOpenView: isExpanded,
            ref: contentRef,
            tabIndex: -1,
            ...commonPopupProps,
            children: [
              TitledWrapper,
              /* @__PURE__ */ jsx(
                Collapse,
                {
                  in: isExpanded,
                  timeout: isExpanded ? 500 : 300,
                  children: renderContent(handleClose)
                }
              )
            ]
          }
        ))
      ]
    }
  );
};
Popup.displayName = COMPONENT_NAME;
export {
  Popup
};
